import React, { CSSProperties } from "react";
import { getTestimonialStyles, shadows } from "../../../config/styleConfig";

interface TestimonialExportProps {
  quote?: string;
  authorName?: string;
  authorTitle?: string;
  authorImage?: string;
  rating?: number;
  backgroundColor?: string;
  textColor?: string;
  showQuotes?: boolean;
  borderRadius?: number;
  textFontFamily?: string;
}

export const TestimonialExport: React.FC<TestimonialExportProps> = ({
  quote = "The care I received at this clinic was exceptional.",
  authorName = "Sarah M.",
  authorTitle,
  authorImage,
  rating = 5,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  showQuotes = true,
  borderRadius = 12,
  textFontFamily,
}) => {
  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const ffStyle = (text?: string): CSSProperties => textFontFamily ? { fontFamily: `'${textFontFamily}', var(--font-family), sans-serif` } : (text && !hasThai(text)) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {};
  const safeRating = rating || 0;
  const styles = getTestimonialStyles(false);

  const containerStyle: CSSProperties = {
    padding: `${styles.padding}px`,
    backgroundColor,
    borderRadius: `${borderRadius}px`,
    boxShadow: shadows.card,
    overflow: "hidden",
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  };

  const starStyle = (filled: boolean): CSSProperties => ({
    width: styles.stars.size,
    height: styles.stars.size,
    color: filled ? "#facc15" : "#d1d5db",
    flexShrink: 0,
  });

  return (
    <div style={containerStyle} className="testimonial-card">
      {safeRating > 0 && (
        <div style={{ display: "flex", gap: "2px", marginBottom: "16px", flexWrap: "wrap" }}>
          {[...Array(5)].map((_, i) => (
            <svg key={i} style={starStyle(i < safeRating)} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
      )}

      <div style={{ position: "relative", minWidth: 0 }}>
        {showQuotes && (
          <span
            style={{
              position: "absolute",
              top: styles.quoteMark.top,
              left: styles.quoteMark.left,
              fontSize: styles.quoteMark.fontSize,
              opacity: 0.2,
              color: textColor,
              lineHeight: 1,
            }}
          >
            "
          </span>
        )}
        <p
          style={{
            fontSize: styles.quote.fontSize,
            fontStyle: "italic",
            marginBottom: `${styles.quote.marginBottom}px`,
            marginTop: 0,
            position: "relative",
            zIndex: 10,
            color: textColor,
            wordWrap: "break-word",
            overflowWrap: "break-word",
            lineHeight: 1.6,
            ...ffStyle(quote),
          }}
        >
          {quote}
        </p>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: `${styles.author.gap}px`,
          minWidth: 0,
        }}
      >
        {authorImage && (
          <img
            src={authorImage}
            alt={authorName}
            style={{
              width: styles.author.imageSize,
              height: styles.author.imageSize,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <span
            style={{
              fontWeight: 600,
              color: textColor,
              fontSize: styles.author.fontSize,
              display: "block",
              ...ffStyle(authorName),
            }}
          >
            {authorName}
          </span>
          {authorTitle && (
            <span
              style={{
                fontSize: styles.author.titleFontSize,
                opacity: 0.7,
                color: textColor,
                display: "block",
                ...ffStyle(authorTitle || ""),
              }}
            >
              {authorTitle}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
